//packages
import React from 'react'
import ReactDOM from 'react-dom'
import { RouterProvider } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { Provider } from 'react-redux'
import axios from 'axios'

// Lazy load PayPal only when needed
const PayPalProvider = React.lazy(() =>
  import('./providers/PayPalProvider').then(module => ({ default: module.PayPalProvider }))
)

// Defer Google Analytics with optimized loading
const initAnalytics = () => {
  import('react-ga4').then((ReactGA) => {
    const TRACKING_ID = 'G-G6P9RF1KTS'
    ReactGA.default.initialize(TRACKING_ID)
    ReactGA.default.send({ hitType: 'pageview', page: window.location.pathname })
  })
}

// Initialize analytics with idle callback to prevent forced reflows
if (typeof window !== 'undefined') {
  loadAnalyticsWithIdleCallback(initAnalytics, 3000)
}

import router from 'app/route/Routes'
import store from 'app/reducers/rootReducer'
import 'react-toastify/dist/ReactToastify.css'
import './App.css'
import { initializeAssetPreloading } from 'app/utils/cacheUtils'
import { initializeAnalyticsOptimizations, loadAnalyticsWithIdleCallback } from 'app/utils/analyticsUtils'
import {
  clearPersistedSearchFilters,
  isBrowserReload,
} from 'app/utils/candidateFilterStorage'

if (isBrowserReload()) {
  clearPersistedSearchFilters()
}
import { configureApiClient } from 'app/clients/configureApiClient'
import ErrorBoundary from 'components/ErrorBoundary'
import { initializeErrorMonitoring } from 'app/utils/errorMonitoring'

configureApiClient(axios)
initializeErrorMonitoring()

const HTML_TAG_INPUT_PATTERN = /<\s*\/?\s*[a-z][^>]*>?/i
const HTML_TAG_INPUT_PATTERN_GLOBAL = new RegExp(HTML_TAG_INPUT_PATTERN.source, 'gi')
const TEXT_INPUT_TYPES = new Set([
  '',
  'text',
  'search',
  'url',
  'tel',
  'email',
  'password',
  'number',
])

const isTextEntryElement = (element) => {
  if (!element) return false

  if (element instanceof HTMLTextAreaElement) return true
  if (element instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has((element.type || '').toLowerCase())
  }

  return false
}

const containsHtmlTagInput = (text = '') => HTML_TAG_INPUT_PATTERN.test(text)

const stripHtmlTagInput = (text = '') =>
  text.replace(HTML_TAG_INPUT_PATTERN_GLOBAL, '').trim()

// Initialize asset preloading for better performance
initializeAssetPreloading()

// Initialize analytics optimizations to prevent forced reflows
initializeAnalyticsOptimizations()

const BrowserCloseStorageCleanup = () => {
  React.useEffect(() => {
    window.addEventListener('beforeunload', clearPersistedSearchFilters)

    return () => {
      window.removeEventListener('beforeunload', clearPersistedSearchFilters)
    }
  }, [])

  return null
}

const GlobalInputScriptGuard = () => {
  React.useEffect(() => {
    const handleInputCapture = (event) => {
      const target = event.target
      if (!isTextEntryElement(target)) return

      const nextValue = target.value ?? ''
      if (!containsHtmlTagInput(nextValue)) return

      target.value = stripHtmlTagInput(nextValue)
    }

    const handlePasteCapture = (event) => {
      const target = event.target
      if (!isTextEntryElement(target)) return

      const pastedText = event.clipboardData?.getData('text') ?? ''
      if (!containsHtmlTagInput(pastedText)) return

      const safeText = stripHtmlTagInput(pastedText)
      event.preventDefault()

      if (typeof target.setRangeText === 'function') {
        const start = target.selectionStart ?? 0
        const end = target.selectionEnd ?? start
        target.setRangeText(safeText, start, end, 'end')
      } else {
        target.value += safeText
      }

      target.dispatchEvent(new Event('input', { bubbles: true }))
    }

    document.addEventListener('input', handleInputCapture, true)
    document.addEventListener('paste', handlePasteCapture, true)

    return () => {
      document.removeEventListener('input', handleInputCapture, true)
      document.removeEventListener('paste', handlePasteCapture, true)
    }
  }, [])

  return null
}

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <ErrorBoundary>
        <React.Suspense fallback={<div>Loading...</div>}>
        <BrowserCloseStorageCleanup />
        <GlobalInputScriptGuard />
        {/* <PayPalProvider> */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ zIndex: 9999 }}
        />
        <RouterProvider router={router} />
        {/* </PayPalProvider> */}
        </React.Suspense>
      </ErrorBoundary>
    </Provider>
  </React.StrictMode>,
  document.getElementById('root')
)
